import { Button, ButtonGroup, Chip } from '@mui/material';
import { GetRowIdParams, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useEffect, useRef, useState } from 'react';
import { MarkItViewApi } from '../../../../Apis/Apis';
import { CompanyModel, MarketViewUserModel } from '../../../../Generated/Mark-It-View-Api';
import { SortDirection } from '../../../../Generated/Mark-It-View-Api';
import { useLoadingState } from '../../../../Hooks';
import { StoneXGrid, StoneXLoading, StoneXRow } from '../../../StoneX';
import StoneXMainPage from '../../../StoneX/StoneXMainPage/StoneXMainPage';
import { StoneXModal, StoneXTextField } from '../../../StoneXMui';
import { MarketViewUsersTable } from '../Users/MarketViewUsersTable';
import { AddUserRequest, saveMarketViewUser, updateMarketViewUserEnabled } from '../Users/MarketViewUserAdminLogic';
import { UpdateUserModal } from '../Users/UpdateUserModal';
import UpdateCompanyModal from './UpdateCompanyModal';

export default function Companies() {
  const gridRef = useRef<AgGridReact<CompanyModel>>(null);
  const [companies, setCompanies] = useState<CompanyModel[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalCompany, setModalCompany] = useState<CompanyModel | undefined>();
  const [companyUsersModalOpen, setCompanyUsersModalOpen] = useState<boolean>(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyModel | undefined>();
  const [selectedCompanyUsers, setSelectedCompanyUsers] = useState<MarketViewUserModel[]>([]);
  const [userModalOpen, setUserModalOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<MarketViewUserModel | undefined>();
  const companiesLoadingState = useLoadingState();
  const companyUsersLoadingState = useLoadingState();

  function loadCompanies() {
    MarkItViewApi.listCompanies(companiesLoadingState.setLoadingState, 'name', SortDirection.Asc)
      .then((response) => setCompanies(response.data.rows ?? []));
  }

  useEffect(() => {
    loadCompanies();
  }, []);

  function openModal() {
    setModalCompany(undefined);
    setModalOpen(true);
  }

  function editCompany(companyGuid: string) {
    const company = companies.find((row) => row.companyGuid === companyGuid);

    if (!company) {
      return;
    }

    setModalCompany(company);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setModalCompany(undefined);
  }

  function onCompanySaved(company: CompanyModel, isNew: boolean) {
    if (isNew) {
      setCompanies((previous) => [...previous, company]);
    } else {
      setCompanies((previous) =>
        previous.map((c) => (c.companyGuid === company.companyGuid ? company : c))
      );
    }
    closeModal();
  }

  function onFilterUpdate(filterText: string) {
    gridRef.current!.api.setGridOption('quickFilterText', filterText);
  }

  function updateCompanyEnabledStatus(company: CompanyModel) {
    MarkItViewApi.updateCompanyEnabled(null, company.companyGuid, company.isDisabled).then(() => {
      loadCompanies();
    });
  }

  function openCompanyUsersModal(company: CompanyModel) {
    setSelectedCompany(company);
    setCompanyUsersModalOpen(true);
    setSelectedCompanyUsers([]);

    MarkItViewApi.listMarketViewUsers(companyUsersLoadingState.setLoadingState, undefined, company.companyGuid, true)
      .then((response) => {
        const rows = response.data.rows ?? [];
        setSelectedCompanyUsers(rows.filter((user) => user.companyGuid === company.companyGuid));
      });
  }

  function closeCompanyUsersModal() {
    setCompanyUsersModalOpen(false);
    setSelectedCompany(undefined);
    setSelectedCompanyUsers([]);
  }

  function updateUser(user: MarketViewUserModel) {
    setSelectedUser(user);
    setUserModalOpen(true);
  }

  function closeUserModal() {
    setUserModalOpen(false);
    setSelectedUser(undefined);
  }

  function saveUser(request: AddUserRequest) {
    saveMarketViewUser(request).then((result) => {
      setSelectedCompanyUsers((previous) => {
        if (result.isNew) {
          return [...previous, result.user];
        }

        return previous.map((row) =>
          row.marketViewUserGuid === result.user.marketViewUserGuid
            ? result.user
            : row,
        );
      });

      loadCompanies();
    });
  }

  function updateEnabledStatus(user: MarketViewUserModel) {
    updateMarketViewUserEnabled(user).then(() => {
      setSelectedCompanyUsers((previous) =>
        previous.map((row) =>
          row.marketViewUserGuid === user.marketViewUserGuid
            ? { ...row, isDisabled: !row.isDisabled }
            : row,
        ),
      );

      loadCompanies();
    });
  }

  const MAX_CHIPS = 8;

  const columnDefs = [
    {
      headerName: 'Name',
      field: 'name',
      sortable: true,
      flex: 2,
    },
    {
      headerName: 'Users',
      field: 'activeUsers',
      sortable: true,
      width: 140,
      cellRenderer: (params: ICellRendererParams<CompanyModel>) => (
        <Button variant="text" onClick={() => openCompanyUsersModal(params.data!)}>
          {`${params.data?.activeUsers ?? 0} / ${params.data?.totalUsers ?? 0}`}
        </Button>
      ),
    },
    {
        headerName: 'Disabled',
        field: 'isDisabled',
        sortable: true,
        width: 180,
        cellRenderer: (params: ICellRendererParams<CompanyModel>) =>
            params.data?.isDisabled ? <Chip variant="alert" label="DISABLED" /> : null,
    },
    {
      headerName: 'Applications',
      field: 'marketViewApplications',
      sortable: true,
      flex: 2,
      cellRenderer: (params: ICellRendererParams<CompanyModel>) => {
        const apps = params.value as string[] | undefined;
        if (!apps || apps.length === 0) {
          return null;
        }
        const displayApps = apps.slice(0, MAX_CHIPS);
        const extraCount = apps.length - MAX_CHIPS;
        return (
          <>
            {displayApps.map((app) => (
              <Chip key={app} label={app} size="small" style={{ marginRight: 4 }} />
            ))}
            {apps.length > MAX_CHIPS && (
              <Chip key="more" label={`+${extraCount}`} size="small" style={{ marginRight: 4, background: '#eee', fontWeight: 600 }} />
            )}
          </>
        );
      },
    },
    {
      headerName: 'Product Access',
      field: 'productAccess',
      sortable: true,
      flex: 2,
      cellRenderer: (params: ICellRendererParams<CompanyModel>) => {
        const entries = params.data?.productAccess;
        if (!entries || entries.length === 0) {
          return null;
        }
        const displayEntries = entries.slice(0, MAX_CHIPS);
        const extraCount = entries.length - MAX_CHIPS;
        return (
          <>
            {displayEntries.map((entry) => (
              <Chip key={entry.commodityType} label={entry.commodityType} size="small" style={{ marginRight: 4 }} />
            ))}
            {entries.length > MAX_CHIPS && (
              <Chip key="more" label={`+${extraCount}`} size="small" style={{ marginRight: 4, background: '#eee', fontWeight: 600 }} />
            )}
          </>
        );
      },
    },
    {
      headerName: 'Actions',
      field: 'actions',
      width: 210,
      cellRenderer: (params: ICellRendererParams<CompanyModel>) => {
        return (
          <ButtonGroup variant="text">
            <Button onClick={() => editCompany(params.data!.companyGuid)}>Edit</Button>
            <Button onClick={() => updateCompanyEnabledStatus(params.data!)}>{params.data?.isDisabled ? 'Enable' : 'Disable'}</Button>
          </ButtonGroup>
        );
      },
    },
  ];

  if (companiesLoadingState.isLoading()) {
    return (
      <StoneXMainPage>
        <StoneXLoading show />
      </StoneXMainPage>
    );
  }

  return (
    <StoneXMainPage>
      <StoneXRow align="baseline">
        <h1>Companies</h1>
        <Button className="pull-right" variant="outlined" onClick={openModal}>
          Add
        </Button>
      </StoneXRow>

      <StoneXRow>
        <StoneXTextField placeholder="Filter..." onUpdate={onFilterUpdate} />
      </StoneXRow>

      <StoneXRow align="end">
        <div style={{ width: '100%' }}>
          <StoneXGrid
            ref={gridRef}
            getRowId={(params: GetRowIdParams) => params.data.companyGuid}
            columns={columnDefs}
            rows={companies}
            gridOptions={{ pagination: true, paginationPageSize: 50 }}
            hideGridOptions
          />
        </div>
      </StoneXRow>

      <UpdateCompanyModal open={modalOpen} onClose={closeModal} onCompanySaved={onCompanySaved} company={modalCompany} />

      <StoneXModal
        header={`Users: ${selectedCompany?.name ?? ''}`}
        open={companyUsersModalOpen}
        onClose={closeCompanyUsersModal}
        width="100%"
      >
        {companyUsersLoadingState.isLoading() && <StoneXLoading show />}

        {!companyUsersLoadingState.isLoading() && (
          <MarketViewUsersTable
            users={selectedCompanyUsers}
            companies={companies}
            showCompanyColumn={false}
            showActions
            onUpdateUser={updateUser}
            onToggleUserEnabled={updateEnabledStatus}
          />
        )}
      </StoneXModal>

      <UpdateUserModal
        open={userModalOpen}
        onClose={closeUserModal}
        onUserSave={saveUser}
        user={selectedUser}
        companies={companies}
      />
    </StoneXMainPage>
  );
}
